import React, { useState, useEffect, useMemo } from 'react';
import SEO from '../components/Common/SEO';
import { Tour } from '../types';
import TourCard from '../components/Tours/TourCard';
import { tourService } from '../services/tourService';
import { useTheme } from '../contexts/ThemeContext';
import { usePricing } from '../contexts/PricingContext';
import { useCategoryOrder } from '../contexts/CategoryOrderContext';
import { applyRoundRobinOrder } from '../utils/applyRoundRobinOrder';
import { filterTours } from '../utils/filterTours';
import PricingFilter from '../components/Common/PricingFilter';
import { TourCardSkeleton } from '../components/Common/Skeleton';

const Tours: React.FC = () => {
  const { actualTheme } = useTheme();
  const pricingContext = usePricing();
  const { featuredCategories } = useCategoryOrder();
  const {
    searchQuery,
    category,
    duration,
    difficulty,
    budgetRange
  } = pricingContext;

  const [allTours, setAllTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // One-time cleanup for stale ghost data
    const cleanupGhostData = () => {
      const ghostClearingKey = 'st_ghost_cleanup_v2';
      if (!localStorage.getItem(ghostClearingKey)) {
        console.log('🧹 One-time cleanup of stale tour categories...');
        localStorage.removeItem('tourCategories');
        localStorage.removeItem('featuredCategoriesOrder');
        localStorage.setItem(ghostClearingKey, 'true');
        // Force refresh
        window.location.reload();
      }
    };
    cleanupGhostData();

    const loadTours = async () => {
      try {
        setLoading(true);
        const activeTours = await tourService.getActiveTours();
        // Apply round-robin interleaving using the admin-ordered category sequence
        const orderedTours = applyRoundRobinOrder(activeTours, featuredCategories);
        setAllTours(orderedTours);
      } catch (error) {
        console.error('Error loading tours:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTours();
  }, [featuredCategories]);



  // Sync with storage for admin changes
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key && (e.key.startsWith('tours_') || e.key === 'featuredToursOrder' || e.key.startsWith('categoryToursOrder_') || e.key === 'featuredCategoriesOrder')) {
        try {
          const activeTours = await tourService.getActiveTours();
          setAllTours(applyRoundRobinOrder(activeTours, featuredCategories));
        } catch (error) {
          console.error('Error reloading tours on storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [featuredCategories]);

  // Consolidated filtering logic
  const filteredTours = useMemo(() => {
    return filterTours(allTours, pricingContext);
  }, [allTours, pricingContext]);

  if (loading && allTours.length === 0) {
    return (
      <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <TourCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
      <SEO
        title="All Safari Tours"
        description="Explore our full collection of carefully curated safari experiences in Kenya and Tanzania. Find your perfect African adventure."
        keywords={['safari tours', 'wildlife tours', 'african adventures', 'kenya safaris', 'tanzania safaris']}
      />
      {/* Floating Header Section */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pt-6 sm:pt-8 md:pt-10 lg:pt-12">
        <div id="tours-header" className={`rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 shadow-lg ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-primary-900 to-primary-800 border border-primary-700/50'
          : 'bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/30'
          } text-white`}>
          <div className="text-center">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 md:mb-4 px-2">
              Safari Tours
            </h1>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-primary-100 max-w-2xl mx-auto leading-relaxed px-2">
              Discover our collection of carefully curated safari experiences,
              each designed to provide unforgettable encounters with Africa's magnificent wildlife.
            </p>
          </div>
        </div>
      </div>

      {/* Global Consolidated Filter */}
      <div className="mt-8 min-h-[100px]">
        <PricingFilter />
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-12 pb-24 min-h-[600px]">
        {loading && allTours.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <TourCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* Results Info */}
            <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h2 className={`text-xl font-bold ${actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-800'}`}>
                Showing {filteredTours.length} {filteredTours.length === 1 ? 'Tour' : 'Tours'}
                {category && category !== 'All' && ` in ${category}`}
              </h2>
              {(searchQuery || category !== 'All' || duration !== 'Any Duration' || difficulty !== 'Any Level') && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-primary-600 font-bold bg-primary-50 dark:bg-primary-900/30 px-3 py-1 rounded-full border border-primary-200 dark:border-primary-800">
                    Filters Applied
                  </span>
                </div>
              )}
            </div>

            {/* Tours Grid */}
            <div id="tour-grid-view" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {filteredTours.map((tour, index) => (
                <div key={tour.id} id={index === 0 ? 'tour-sample-card' : undefined}>
                  <TourCard
                    tour={tour}
                    showQuickBook={true}
                  />
                </div>
              ))}
            </div>

            {/* No Results */}
            {filteredTours.length === 0 && (
              <div className="text-center py-24 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-neutral-300 dark:border-slate-700 shadow-sm">
                <div className="text-6xl mb-6 opacity-20">🔍</div>
                <h3 className={`text-2xl font-bold mb-3 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-800'}`}>
                  No matching safaris found
                </h3>
                <p className={`max-w-md mx-auto ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'}`}>
                  We couldn't find any tours matching your current criteria. Try adjusting your filters or clearing your search query.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Tours;